import { useEffect, useRef, useState } from 'react';
import { KeyRound, Copy, Check, X, Eye, EyeOff } from 'lucide-react';
import { unlockPrivateKey } from '../../../core/crypto';
import { useVault } from '../../../core/lib/vault';
import { getAccessToken } from '../../../core/lib/api';

const REVEAL_AUTO_HIDE_MS = 30_000;
const CLIPBOARD_AUTO_CLEAR_MS = 30_000;

/**
 * Revealing a password field requires the user to re-authenticate with their
 * Master Code even when the vault is already unlocked. This defeats a walk-up
 * attacker who finds an unlocked session — they need the Master Code again
 * before any plaintext password is displayed.
 *
 * Once revealed the modal auto-hides after 30 seconds. Clipboard copies are
 * scheduled to clear after 30 seconds (only if they haven't been overwritten
 * by the user in the meantime). Every reveal generates an audit log event.
 */
export function RevealPasswordModal({
  id,
  label,
  value,
  onClose,
}: {
  /** Stable field id for audit-log disambiguation. Empty for legacy un-backfilled fields. */
  id: string;
  label: string;
  value: string;
  onClose: () => void;
}) {
  const encryptedPrivateKey = useVault((s) => s.encryptedPrivateKey);
  const [masterCode, setMasterCode] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [showValue, setShowValue] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(REVEAL_AUTO_HIDE_MS / 1000);
  const clipboardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Countdown + auto-hide after reveal.
  useEffect(() => {
    if (!revealed) return;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          onClose();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [revealed, onClose]);

  // Clear clipboard on unmount if we set it.
  useEffect(() => {
    return () => {
      if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
    };
  }, []);

  async function confirm() {
    if (!encryptedPrivateKey) return;
    setLoading(true);
    setError(null);
    try {
      // Re-derive KEK from Master Code and verify it decrypts the user's private
      // key. The decrypted key is zeroed immediately — this is proof of
      // knowledge of the Master Code, not a long-lived unlock.
      const priv = await unlockPrivateKey(encryptedPrivateKey, masterCode);
      priv.fill(0);
      setRevealed(true);
      setMasterCode(''); // wipe from React state
      // Fire-and-forget audit log (best-effort; doesn't block reveal).
      const tok = getAccessToken();
      void fetch('/api/audit/password-revealed', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: JSON.stringify({ id, label }),
        credentials: 'same-origin',
      }).catch(() => undefined);
    } catch (err: any) {
      setError(err?.message ?? 'Incorrect Master Code.');
    } finally {
      setLoading(false);
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      if (clipboardTimerRef.current) clearTimeout(clipboardTimerRef.current);
      clipboardTimerRef.current = setTimeout(async () => {
        try {
          // Only clear if the clipboard still contains our value.
          const current = await navigator.clipboard.readText().catch(() => '');
          if (current === value) await navigator.clipboard.writeText('');
        } catch {
          /* ignore */
        }
      }, CLIPBOARD_AUTO_CLEAR_MS);
    } catch {
      setError('Clipboard access was denied by the browser.');
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-md p-5">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="text-dragon-500" size={18} />
          <h2 className="font-semibold">{label || 'Password'}</h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1">
            <X size={14} />
          </button>
        </div>
        {!revealed ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-300">Re-enter your Master Code to reveal this password.</p>
            {/* Suppress password-manager autofill prompts. `autoComplete="off"`
                alone is widely ignored on `type="password"` fields; the
                `data-*-ignore` attrs are the canonical opt-outs for 1Password
                / LastPass / Bitwarden / Dashlane. `data-form-type="other"`
                tells generic PMs this isn't a login. The `name` is
                deliberately not "password" / "pwd" / "passwd" — those
                keywords trigger heuristic capture in some PMs. */}
            <input
              type="password"
              className="input"
              value={masterCode}
              onChange={(e) => setMasterCode(e.target.value)}
              autoFocus
              autoComplete="off"
              name="vault-mc-reveal"
              data-1p-ignore="true"
              data-lpignore="true"
              data-bwignore="true"
              data-dashlane-ignore="true"
              data-form-type="other"
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirm();
              }}
            />
            {error && <div className="text-sm text-red-400">{error}</div>}
            <button className="btn-primary w-full" onClick={confirm} disabled={loading}>
              {loading ? 'Verifying…' : 'Reveal'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-md border border-ink-700 bg-ink-800 p-3 font-mono text-sm break-all select-all">
              {showValue ? value : '•'.repeat(Math.min(Math.max(value.length, 8), 40))}
            </div>
            <div className="flex gap-2">
              <button
                className="btn-ghost flex-1"
                onClick={() => setShowValue((v) => !v)}
              >
                {showValue ? (
                  <>
                    <EyeOff size={14} className="mr-1" /> Hide
                  </>
                ) : (
                  <>
                    <Eye size={14} className="mr-1" /> Show
                  </>
                )}
              </button>
              <button className="btn-ghost flex-1" onClick={copy}>
                {copied ? (
                  <>
                    <Check size={14} className="mr-1" /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} className="mr-1" /> Copy
                  </>
                )}
              </button>
            </div>
            <p className="text-xs text-ink-500 text-center">
              Auto-closes in {secondsLeft}s · Clipboard clears after 30s
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
